import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Assessment_Activity_Status_Enum,
  Assessment_Activity_Type_Enum,
  InsertAssessmentActivityWithLinkedItemsMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTRPC } from '@/utils/trpc';

/**
 * tRPC input type for insertActivity.
 * Uses explicit type instead of GraphQL MutationVariables to avoid
 * the codegen `Array<T> | T` issue with inline variables.
 */
export type InsertAssessmentActivityTRPCInput = {
  ActivityType: Assessment_Activity_Type_Enum;
  ParentId: string;
  Status: Assessment_Activity_Status_Enum;
  Summary?: string | null;
  Title: string;
  CompletionDate?: string | null;
  AssignedUser?: string | null;
  LinkedItemIds: string[];
  CustomAttributeData?: CustomAttributeData | null;
  IsRCSA?: boolean;
  RiskId?: string | null;
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
};

const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): InsertAssessmentActivityWithLinkedItemsMutation => {
  return {
    insertAssessmentActivityWithLinkedItems: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useInsertAssessmentActivityWithLinkedItemsTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.assessment.insertActivity.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.assessment.activityRegister.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.assessment.activityById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.assessment.assessmentActivitiesByParentId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.assessment.assessmentRCSAActivitiesByAssessmentId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.assessment.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.assessment.oversightActivityRegister.queryKey(),
        });
        // COI-471 parity: Apollo twin also evicts `internal_audit_entity`,
        // `internal_audit_report` and `oversight_assessment`.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.internalAuditEntity.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.internalAuditReport.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.oversight.pathKey(),
          }),
        ]);
      },
    }),
  });

  return {
    insertAssessmentActivity: async (
      variables: InsertAssessmentActivityTRPCInput
    ): Promise<InsertAssessmentActivityWithLinkedItemsMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
